import type {
    ComponentStageData,
    ComponentStageHandlerResults,
    MobileAlgoPlugin,
    MobileAlgoPluginInitializationArgs,
    PluginContext,
    PluginHeuristicsRegistry,
    PluginStageHandler
} from '../../types'
import {createAdaptiveHeuristic, findHeuristicFromTree, isGridContainerLayout} from '../common'
import {heightTree, verticalSpacingTree, rowsFlushTree, rowsAdaptiveTree} from './layoutModels'

export const createPlugin = ({stages, readOnlyExtensionAPI}: MobileAlgoPluginInitializationArgs): MobileAlgoPlugin => {
    const calculateVerticalSpacing: PluginStageHandler<ComponentStageData> = (
        componentStageData: ComponentStageData,
        contextHelper: PluginContext
    ) => {
        const heuristicToApply = findHeuristicFromTree(
            verticalSpacingTree,
            componentStageData,
            contextHelper,
            readOnlyExtensionAPI
        )
        if (!heuristicToApply) {
            return
        }
        heuristicToApply.apply(componentStageData, contextHelper, readOnlyExtensionAPI)

        return {appliedHeuristics: [heuristicToApply.heuristicCode]}
    }

    const calculateHeight: PluginStageHandler<ComponentStageData> = (
        componentStageData: ComponentStageData
    ): ComponentStageHandlerResults | undefined => {
        const heuristicToApply = findHeuristicFromTree(heightTree, componentStageData, undefined, readOnlyExtensionAPI)
        if (!heuristicToApply) {
            return
        }
        heuristicToApply.apply(componentStageData, undefined, readOnlyExtensionAPI)

        return {appliedHeuristics: [heuristicToApply.heuristicCode]}
    }

    const setContainerRows: PluginStageHandler<ComponentStageData> = (componentStageData: ComponentStageData) => {
        if (!isGridContainerLayout(componentStageData)) {
            return
        }
        const heuristicToApply = findHeuristicFromTree(
            rowsFlushTree,
            componentStageData,
            undefined,
            readOnlyExtensionAPI
        )

        if (!heuristicToApply) {
            return
        }

        heuristicToApply.apply(componentStageData, undefined, readOnlyExtensionAPI)

        return {appliedHeuristics: [heuristicToApply.heuristicCode]}
    }

    const setContainerRowsAdaptive: PluginStageHandler<ComponentStageData> = (
        componentStageData: ComponentStageData
    ) => {
        if (!isGridContainerLayout(componentStageData)) {
            return
        }
        const heuristicToApply = findHeuristicFromTree(
            rowsAdaptiveTree,
            componentStageData,
            undefined,
            readOnlyExtensionAPI
        )

        if (!heuristicToApply) {
            return
        }

        heuristicToApply.apply(componentStageData, undefined, readOnlyExtensionAPI)

        return {appliedHeuristics: [heuristicToApply.heuristicCode]}
    }

    const register = (registry: PluginHeuristicsRegistry) => {
        registry.register(stages.VERTICAL_LAYOUT_SCALE, createAdaptiveHeuristic(calculateHeight))
        registry.register(stages.VERTICAL_LAYOUT_SCALE, createAdaptiveHeuristic(calculateVerticalSpacing))
        registry.register(
            stages.VERTICAL_LAYOUT_SCALE,
            createAdaptiveHeuristic(setContainerRows, setContainerRowsAdaptive)
        )
    }

    const name = 'verticalScaleAndSize'
    return {
        register,
        name,
        dependencies: ['horizontalScaleAndSize', 'order']
    }
}
